import type { WsEvent } from '@hotel-manager/shared-types';

/**
 * HM-071 — WS client con auto-reconnect (exponential backoff).
 *
 * Usa cookie auth (la connessione include `credentials`-like via stesso origin).
 * Per dev: VITE_WS_URL ?? ws://localhost:3001/ws
 */
export type WsStatus = 'connecting' | 'open' | 'closed' | 'reconnecting';

export type WsClient = {
  status(): WsStatus;
  on(handler: (ev: WsEvent) => void): () => void;
  onStatus(handler: (s: WsStatus) => void): () => void;
  close(): void;
};

const BACKOFF_STEPS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_BACKOFF_MS = 30_000;

export type CreateWsOpts = {
  url: string;
  /** Iniezione opzionale per test (jsdom non supporta WebSocket reale). */
  WebSocketImpl?: typeof WebSocket;
  /** Disable auto-reconnect (test). */
  autoReconnect?: boolean;
};

export function createWsClient(opts: CreateWsOpts): WsClient {
  const Impl = opts.WebSocketImpl ?? globalThis.WebSocket;
  const handlers = new Set<(ev: WsEvent) => void>();
  const statusHandlers = new Set<(s: WsStatus) => void>();
  let socket: WebSocket | null = null;
  let attempt = 0;
  let closed = false;
  let status: WsStatus = 'connecting';

  function setStatus(s: WsStatus) {
    if (status === s) return;
    status = s;
    for (const h of statusHandlers) h(s);
  }

  function connect() {
    if (closed) return;
    setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
    try {
      socket = new Impl(opts.url);
    } catch (_err) {
      scheduleReconnect();
      return;
    }
    socket.addEventListener('open', () => {
      attempt = 0;
      setStatus('open');
    });
    socket.addEventListener('message', (e) => {
      try {
        const parsed = JSON.parse(typeof e.data === 'string' ? e.data : String(e.data)) as WsEvent;
        for (const h of handlers) h(parsed);
      } catch {
        // ignore malformed
      }
    });
    socket.addEventListener('close', () => {
      setStatus('closed');
      scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    });
  }

  function scheduleReconnect() {
    if (closed) return;
    if (opts.autoReconnect === false) return;
    const delay =
      BACKOFF_STEPS_MS[Math.min(attempt, BACKOFF_STEPS_MS.length - 1)] ?? MAX_BACKOFF_MS;
    attempt += 1;
    setStatus('reconnecting');
    setTimeout(connect, delay);
  }

  connect();

  return {
    status: () => status,
    on(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    onStatus(handler) {
      statusHandlers.add(handler);
      handler(status);
      return () => statusHandlers.delete(handler);
    },
    close() {
      closed = true;
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    },
  };
}

export function defaultWsUrl(): string {
  const env = (import.meta.env.VITE_WS_URL as string | undefined) ?? '';
  if (env) return env;
  return 'ws://localhost:3001/ws';
}
